import { useProductContractHandoff } from "@/api/services/admin/product-contract";
import { createRegisterSupportOrganizationApi, updateRegisterSupportOrganizationApi } from "@/api/services/admin/rso";
import { useEnum } from "@/api/services/global/enum";
import { FooterActionBar } from "@/components/commons/FooterActionBar";
import { Button } from "@/components/elements/Button";
import { CheckboxGroup } from "@/components/elements/CheckboxGroup";
import { Form } from "@/components/elements/Form";
import { FormField } from "@/components/elements/FormField";
import { InnerBox } from "@/components/elements/InnerBox";
import { Input } from "@/components/elements/Input";
import { InputDatePicker } from "@/components/elements/InputDatePicker";
import { Select } from "@/components/elements/Select";
import { Textarea } from "@/components/elements/Textarea";
import { API_SUCCESS_MESSAGE } from "@/constants/common";
import { QUERY_KEYS } from "@/constants/queryKeys";
import { registerSupportOrganizationSchema } from "@/features/rso/schema/registerSupportOrganizationSchema";
import { TRegisterSupportOrganizationForm } from "@/features/rso/types/registerSupportOrganizationForm";
import { convertRsoFormToCreateReq, convertRsoFormToUpdateReq } from "@/features/rso/utils/converts";
import { useToast } from "@/hooks/useToast";
import { useTriggerValidate } from "@/hooks/useTriggerValidate";
import { AppRoutes } from "@/routes/config";
import { ERSOContractStatus, ERsoOrganizationType } from "@/types/enum";
import { CONTRACT_TOKEN_KEY } from "@/utils/constants";
import { dayjs } from "@/utils/dayjs";
import { setErrorForm, showError } from "@/utils/error";
import { getCenterFrom, getDynamicRoute, redirectToCenter } from "@/utils/url";
import { yupResolver } from "@hookform/resolvers/yup";
import { Box, Grid, SelectChangeEvent, Stack, styled } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import Cookies from "js-cookie";
import { FC, useEffect, useState } from "react";
import { useForm, useWatch } from "react-hook-form";
import { Link, useNavigate, useParams } from "react-router-dom";

type TRegisterSupportOrganizationFormProps = {
  previousData?: Partial<TRegisterSupportOrganizationForm>;
  backUrl?: string;
};

const initCreateForm = {
  organizationType: [],
  rsoName: "",
  rsoRegistrationNumber: `${dayjs().format("YY")}登-`,
  contractStatus: "",
  contractPlan: "",
  description: "",
  note: "",
};

export const RegisterSupportOrganizationForm: FC<TRegisterSupportOrganizationFormProps> = ({ previousData }) => {
  const currentYearSuffix = dayjs().format("YY");
  const prefix = `${currentYearSuffix}登-`;
  const { contract } = useProductContractHandoff();
  const { data: organizationTypeOptions } = useEnum("ERsoOrganizationType");
  const { id } = useParams();
  const isEditMode = Boolean(id);
  const toast = useToast();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [isSaved, setIsSaved] = useState(false);

  const methods = useForm<TRegisterSupportOrganizationForm>({
    defaultValues: previousData ?? initCreateForm,
    resolver: yupResolver(registerSupportOrganizationSchema),
    mode: "all",
    reValidateMode: "onBlur",
  });
  const { control, setValue } = methods;
  useTriggerValidate({ methods, name: "organizationType", triggerFields: ["rsoRegistrationNumber"] });
  useTriggerValidate({ methods, name: "contractStatus", triggerFields: ["contractStartDate"] });
  useTriggerValidate({ methods, name: "contractStartDate", triggerFields: ["contractEndDate"] });

  const organizationTypeWatchField = useWatch({ control, name: "organizationType" });
  // 登録番号 is mandatory only for 登録支援機関; drives both the asterisk and the schema rule.
  const isRegisteredSupportSelected = (organizationTypeWatchField ?? []).includes(ERsoOrganizationType.REGISTERED_SUPPORT);

  useEffect(() => {
    const rsoName = methods.getValues("rsoName");
    if (!rsoName && contract?.organizationName) {
      methods.resetField("rsoName", { defaultValue: contract.organizationName });
    }
  }, [contract]);

  useEffect(() => {
    if (!isSaved) return;
    if (isEditMode) {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.RSO.FETCH_DETAIL, id] });
      navigate(getDynamicRoute(AppRoutes.rsoDetail, { id }));
      return;
    }
    Cookies.remove(CONTRACT_TOKEN_KEY);
    // 遷移元のcenterパスが分かっていれば、その画面へそのまま戻す。
    const centerFrom = getCenterFrom();
    if (centerFrom) {
      redirectToCenter(centerFrom);
      return;
    }
    if (!contract) {
      const centerOrganizationList = "/organizations";
      redirectToCenter(centerOrganizationList);
      return;
    }
    const centerOrganizationDetail = getDynamicRoute("/organizations/:id", { id: contract.organizationId });
    redirectToCenter(centerOrganizationDetail);
  }, [isSaved]);

  const handleRsoRegistrationNumberChange = (value: string) => {
    // Read 種別 straight from the form rather than the render-scoped watch value: Input's clear
    // button re-fires this handler after setting the field, so a batched 種別 change would
    // otherwise be observed through a stale closure and re-seed the prefix.
    const isRegisteredSupportNow = (methods.getValues("organizationType") ?? []).includes(ERsoOrganizationType.REGISTERED_SUPPORT);

    // Re-seeding the prefix on clear only helps while the field is mandatory. When 登録番号 is
    // optional, a bare prefix would fail the format check, so let the field go genuinely empty.
    if (!value && isRegisteredSupportNow) {
      setValue("rsoRegistrationNumber", prefix);
    } else {
      setValue("rsoRegistrationNumber", value);
    }
  };

  const trialEndDateWatchField = useWatch({ control, name: "trialEndDate" });
  const trialStartDateWatchField = useWatch({ control, name: "trialStartDate" });

  const contractEndDateWatchField = useWatch({ control, name: "contractEndDate" });
  const contractStartDateWatchField = useWatch({ control, name: "contractStartDate" });

  const handleChangeContractStatus = (event: SelectChangeEvent<unknown>) => {
    if (event.target.value === ERSOContractStatus.TRIAL) {
      if (!trialStartDateWatchField) {
        methods.setValue("trialStartDate", dayjs());
      }
      if (!trialEndDateWatchField) {
        const newDate = dayjs(trialStartDateWatchField || dayjs()).add(2, "weeks");
        setValue("trialEndDate", newDate);
      }
    }

    if (event.target.value === ERSOContractStatus.DURING_CONTRACT && !contractStartDateWatchField) {
      methods.setValue("contractStartDate", dayjs());
    }
  };

  const onSubmit = async (data: TRegisterSupportOrganizationForm) => {
    try {
      if (isEditMode) {
        const payload = convertRsoFormToUpdateReq(data);
        const rs = await updateRegisterSupportOrganizationApi(id!, payload);
        toast.success(rs.message ?? API_SUCCESS_MESSAGE);
        setIsSaved(true);
        return;
      }
      if (!contract) {
        navigate(AppRoutes.rsoList);
        return;
      }
      const payload = convertRsoFormToCreateReq(data, contract.organizationId, contract.productContractId);
      const rs = await createRegisterSupportOrganizationApi(payload);
      toast.success(rs.message ?? API_SUCCESS_MESSAGE);
      setIsSaved(true);
    } catch (error) {
      showError(error);
      setErrorForm(methods, error);
    }
  };

  return (
    <Form methods={methods} onSubmit={onSubmit} schema={registerSupportOrganizationSchema}>
      <Box width="814px" mx="auto">
        <InnerBox>
          <Grid container rowSpacing={2} columnSpacing={3}>
            <Grid item xs={12}>
              <FormField control={control} name="organizationType">
                <CheckboxGroupRow>
                  <CheckboxGroup options={organizationTypeOptions} />
                </CheckboxGroupRow>
              </FormField>
            </Grid>
            <Grid item xs={6}>
              <FormField control={control} name="rsoName">
                <Input />
              </FormField>
            </Grid>
            <Grid item xs={6}>
              <FormField control={control} name="rsoRegistrationNumber" required={isRegisteredSupportSelected}>
                <Input onChange={(e) => handleRsoRegistrationNumberChange(e.target.value)} />
              </FormField>
            </Grid>
            <Grid item xs={6}>
              <FormField control={control} name="contractStatus">
                <Select enumName="ERSOContractStatus" onChange={handleChangeContractStatus} />
              </FormField>
            </Grid>
            <Grid item xs={6}>
              <FormField control={control} name="contractPlan">
                <Input />
              </FormField>
            </Grid>
            <Grid item xs={6}>
              <FormField control={control} name="trialStartDate">
                <InputDatePicker
                  maxDate={trialEndDateWatchField ?? undefined}
                  onChange={(value) => {
                    if (!trialEndDateWatchField && !!value) {
                      const newDate = dayjs(value).add(2, "weeks");
                      setValue("trialEndDate", newDate);
                    }
                  }}
                />
              </FormField>
            </Grid>
            <Grid item xs={6}>
              <FormField control={control} name="trialEndDate">
                <InputDatePicker minDate={trialStartDateWatchField ?? undefined} />
              </FormField>
            </Grid>
            <Grid item xs={6}>
              <FormField
                required={methods.watch("contractStatus") === ERSOContractStatus.DURING_CONTRACT.toString()}
                control={control}
                name="contractStartDate"
              >
                <InputDatePicker maxDate={contractEndDateWatchField ?? undefined} />
              </FormField>
            </Grid>
            <Grid item xs={6}>
              <FormField control={control} name="contractEndDate">
                <InputDatePicker minDate={contractStartDateWatchField ?? undefined} />
              </FormField>
            </Grid>
            <Grid item xs={6}>
              <FormField control={control} name="startReportYear">
                <InputDatePicker format="YYYY" views={["year"]} readonly={!!previousData} />
              </FormField>
            </Grid>
            <Grid item xs={6}></Grid>
            <Grid item xs={6}>
              <FormField control={control} name="description">
                <Textarea />
              </FormField>
            </Grid>
            {isEditMode && (
              <Grid item xs={6}>
                <FormField control={control} name="allowedIps">
                  <Textarea />
                </FormField>
              </Grid>
            )}
            <Grid item xs={6}>
              <FormField control={control} name="note">
                <Textarea />
              </FormField>
            </Grid>
          </Grid>
        </InnerBox>
        <FooterActionBar mt={2}>
          <Stack width="100%" justifyContent="space-between" flexDirection="row">
            {isEditMode ? (
              <Button variant="ghost" size="sm" component={Link} to={isEditMode ? getDynamicRoute(AppRoutes.rsoDetail, { id }) : AppRoutes.rsoList}>
                戻る
              </Button>
            ) : (
              <span />
            )}
            <Button type="submit" size="sm">
              {isEditMode ? "保存" : "追加"}
            </Button>
          </Stack>
        </FooterActionBar>
      </Box>
    </Form>
  );
};

// 種別 has only 2 options — lay them out on one row without altering the shared CheckboxGroup.
const CheckboxGroupRow = styled(Box)`
  > .MuiStack-root {
    flex-direction: row;
    gap: 24px;
  }
`;
